/**
 * CDM Saved Queries — Track 2.6 query library.
 *
 * Lists every saved query visible to the org (per-org rows + global
 * defaults), with a JSON editor + "preview" button so authors can
 * iterate before saving. The DSL is small + safe by design — see
 * backend/cdm/query_dsl.py for the column whitelist + op set.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Search, RefreshCw, Plus, Play, Trash2, Save, X, Globe, Edit3,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface SavedQuery {
  id: string
  org_id: string | null
  name: string
  display_name: string
  description: string | null
  archetype: string
  dsl: Record<string, unknown>
  sql_compiled: string | null
  visibility: 'private' | 'org' | 'global'
  created_by: string
  created_at: string | null
  updated_at: string | null
  last_run_at: string | null
}

const ARCHETYPES = ['asset', 'user', 'software', 'event', 'correlation', 'compliance_ticket']

const STARTER_DSL: Record<string, Record<string, unknown>> = {
  asset: {
    archetype: 'asset',
    filters: [],
    sort: [['last_observed_at', 'desc']],
    limit: 100,
  },
  user: {
    archetype: 'user',
    filters: [{ field: 'state', op: 'eq', value: 'active' }],
    sort: [['upn', 'asc']],
    limit: 100,
  },
  software: {
    archetype: 'software',
    filters: [{ field: 'eol_date', op: 'not_null' }],
    sort: [['eol_date', 'asc']],
    limit: 100,
  },
  event: {
    archetype: 'event',
    filters: [],
    aggregation: { op: 'count', field: null, group_by: ['source'] },
    limit: 50,
  },
  correlation: {
    archetype: 'correlation',
    filters: [{ field: 'state', op: 'eq', value: 'open' }],
    sort: [['opened_at', 'desc']],
    limit: 50,
  },
  compliance_ticket: {
    archetype: 'compliance_ticket',
    filters: [{ field: 'state', op: 'eq', value: 'open' }],
    sort: [['opened_at', 'desc']],
    limit: 50,
  },
}

export default function CdmSavedQueriesPage() {
  const [queries, setQueries] = useState<SavedQuery[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<SavedQuery | 'new' | null>(null)
  const [message, setMessage] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/cdm/queries')
      setQueries((r.data?.queries || []) as SavedQuery[])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const remove = async (q: SavedQuery) => {
    if (!confirm(`Delete saved query "${q.name}"?`)) return
    try {
      await api.delete(`/api/cdm/queries/${q.id}`)
      setQueries(prev => prev.filter(x => x.id !== q.id))
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || 'Delete failed')
    }
  }

  const run = async (q: SavedQuery) => {
    try {
      const r = await api.post(`/api/cdm/queries/${q.id}/run`)
      const rows: Record<string, unknown>[] = r.data?.rows || []
      setMessage(`Ran "${q.name}" — ${rows.length} rows in ${r.data?.execution_ms} ms`)
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || 'Run failed')
    }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-3 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Search size={18} className="text-cyan" /> CDM SAVED QUERIES
        </h1>
        <span className="text-[10px] text-text-muted">{queries.length} total</span>
        <button type="button" onClick={() => setEditing('new')} className="btn-ghost ml-auto">
          <Plus size={11} /> New query
        </button>
        <Link to="/app/cdm/dashboards" className="btn-ghost">
          Dashboards
        </Link>
        <button type="button" onClick={() => void load()} disabled={loading} className="btn-ghost">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {message && (
        <div className="card p-2 text-[11px] text-text-secondary">
          {message}
          <button type="button" onClick={() => setMessage('')} className="float-right">
            <X size={11} />
          </button>
        </div>
      )}

      <div className="card p-3">
        {loading && <div className="text-text-muted text-xs">Loading…</div>}
        {!loading && queries.length === 0 && (
          <div className="text-text-muted text-xs">No saved queries yet — create your first one.</div>
        )}
        <div className="space-y-2">
          {queries.map(q => (
            <div key={q.id} className="p-2 rounded border border-border bg-surface-card space-y-1">
              <div className="flex items-center gap-3 flex-wrap">
                <code className="text-xs font-mono font-bold text-text-primary">{q.name}</code>
                {q.org_id === null && (
                  <span className="text-[9px] text-text-muted inline-flex items-center gap-1">
                    <Globe size={9} /> global
                  </span>
                )}
                <span className="text-[10px] text-cyan border border-cyan/30 bg-cyan/10 px-2 py-0.5 rounded">
                  {q.archetype}
                </span>
                <span className="text-[10px] text-text-muted">{q.visibility}</span>
                <span className="text-[10px] text-text-muted">by {q.created_by}</span>
                {q.last_run_at && (
                  <span className="text-[10px] text-text-muted">
                    last run {new Date(q.last_run_at).toLocaleString()}
                  </span>
                )}
                <button type="button" onClick={() => void run(q)} className="btn-ghost ml-auto">
                  <Play size={11} /> Run
                </button>
                <button type="button" onClick={() => setEditing(q)} className="btn-ghost">
                  <Edit3 size={11} /> Edit
                </button>
                <button type="button" onClick={() => void remove(q)} className="btn-ghost text-rose-light">
                  <Trash2 size={11} />
                </button>
              </div>
              <div className="text-[11px] text-text-secondary">{q.display_name}</div>
              {q.description && <div className="text-[10px] text-text-muted">{q.description}</div>}
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <QueryEditor
          query={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load() }}
        />
      )}
    </div>
  )
}


function QueryEditor({
  query, onClose, onSaved,
}: {
  query: SavedQuery | null
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = query === null
  const [name, setName] = useState(query?.name || '')
  const [displayName, setDisplayName] = useState(query?.display_name || '')
  const [description, setDescription] = useState(query?.description || '')
  const [archetype, setArchetype] = useState(query?.archetype || 'asset')
  const [visibility, setVisibility] = useState(query?.visibility || 'private')
  const initialDsl = query?.dsl || STARTER_DSL[archetype] || STARTER_DSL.asset
  const [dslText, setDslText] = useState(JSON.stringify(initialDsl, null, 2))
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[] | null>(null)
  const [previewMs, setPreviewMs] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  // When the user picks a different archetype on new queries, reset the
  // DSL skeleton — only do this for new queries to avoid stomping edits.
  useEffect(() => {
    if (isNew) {
      setDslText(JSON.stringify({ ...(STARTER_DSL[archetype] || STARTER_DSL.asset), archetype }, null, 2))
    }
  }, [archetype, isNew])

  const parsedDsl = useMemo(() => {
    try { return JSON.parse(dslText) } catch { return null }
  }, [dslText])

  const preview = async () => {
    setBusy(true)
    setError('')
    try {
      if (parsedDsl === null) throw new Error('DSL is not valid JSON')
      const r = await api.post('/api/cdm/queries/preview', { archetype, dsl: parsedDsl })
      setPreviewRows((r.data?.rows || []) as Record<string, unknown>[])
      setPreviewMs(r.data?.execution_ms ?? null)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Preview failed')
      setPreviewRows(null)
    } finally { setBusy(false) }
  }

  const save = async () => {
    setBusy(true)
    setError('')
    try {
      if (parsedDsl === null) throw new Error('DSL is not valid JSON')
      if (isNew) {
        await api.post('/api/cdm/queries', {
          name: name.trim(),
          display_name: displayName.trim() || name.trim(),
          description,
          archetype,
          dsl: parsedDsl,
          visibility,
        })
      } else if (query) {
        await api.patch(`/api/cdm/queries/${query.id}`, {
          display_name: displayName,
          description,
          archetype,
          dsl: parsedDsl,
          visibility,
        })
      }
      onSaved()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed')
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="card p-4 w-full max-w-3xl max-h-[90vh] overflow-auto space-y-3">
        <div className="flex items-center gap-2">
          <Edit3 size={14} className="text-cyan" />
          <h2 className="text-sm font-bold text-text-primary">{isNew ? 'New saved query' : `Edit ${query?.name}`}</h2>
          <button type="button" onClick={onClose} className="ml-auto text-text-muted hover:text-text-primary"><X size={12} /></button>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <label className="space-y-1">
            <div className="text-[10px] text-text-muted">Name (no spaces)</div>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={!isNew}
              className="bg-surface-card border border-border rounded px-2 py-1 text-xs w-full disabled:opacity-50"
            />
          </label>
          <label className="space-y-1">
            <div className="text-[10px] text-text-muted">Display name</div>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              className="bg-surface-card border border-border rounded px-2 py-1 text-xs w-full"
            />
          </label>
          <label className="space-y-1 col-span-2">
            <div className="text-[10px] text-text-muted">Description</div>
            <input
              type="text"
              value={description || ''}
              onChange={e => setDescription(e.target.value)}
              className="bg-surface-card border border-border rounded px-2 py-1 text-xs w-full"
            />
          </label>
          <label className="space-y-1">
            <div className="text-[10px] text-text-muted">Archetype</div>
            <select
              value={archetype}
              onChange={e => setArchetype(e.target.value)}
              className="bg-surface-card border border-border rounded px-2 py-1 text-xs w-full"
            >
              {ARCHETYPES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <div className="text-[10px] text-text-muted">Visibility</div>
            <select
              value={visibility}
              onChange={e => setVisibility(e.target.value as SavedQuery['visibility'])}
              className="bg-surface-card border border-border rounded px-2 py-1 text-xs w-full"
            >
              <option value="private">private</option>
              <option value="org">org</option>
              <option value="global">global (admin)</option>
            </select>
          </label>
        </div>

        <div className="space-y-1">
          <div className="text-[10px] text-text-muted">DSL JSON</div>
          <textarea
            value={dslText}
            onChange={e => setDslText(e.target.value)}
            className="bg-black/30 border border-border rounded p-2 text-[10px] font-mono w-full h-64"
            spellCheck={false}
          />
          {parsedDsl === null && <div className="text-rose-light text-[10px]">JSON is not valid</div>}
        </div>

        {error && <div className="text-rose-light text-[11px]">{error}</div>}

        <div className="flex gap-2">
          <button type="button" onClick={() => void preview()} disabled={busy || parsedDsl === null} className="btn-ghost">
            <Play size={11} /> Preview
          </button>
          <button type="button" onClick={() => void save()} disabled={busy || !name.trim() || parsedDsl === null} className="btn-primary ml-auto">
            <Save size={11} /> {isNew ? 'Create' : 'Save'}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>

        {previewRows !== null && (
          <div className="space-y-1">
            <div className="text-[10px] text-text-muted">
              Preview · {previewRows.length} rows · {previewMs ?? 0} ms
            </div>
            <pre className="text-[10px] font-mono whitespace-pre-wrap break-all bg-black/30 p-2 rounded max-h-64 overflow-auto">
              {JSON.stringify(previewRows.slice(0, 50), null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}
